/**
 * Captures the MSAL failures that are otherwise invisible.
 *
 * `MsalProvider` calls `handleRedirectPromise()` and swallows its rejection — its own
 * source says "Errors should be handled by listening to the LOGIN_FAILURE event". Nothing
 * did, so a failed sign-in returned to the app with no account, no error, and no console
 * output, and the SPA simply rendered the sign-in prompt again. That is indistinguishable
 * from a redirect loop, and it cost several rounds of blind diagnosis.
 *
 * The captured error is kept in sessionStorage because the failure arrives *during* the
 * redirect back: whatever is recorded in memory at that moment may be discarded before any
 * component renders. Keeping it lets the UI show what went wrong on the load that follows.
 */
import {
  EventType,
  type AuthError,
  type EventMessage,
  type IPublicClientApplication,
} from '@azure/msal-browser';

const KEY = 'orm.auth.lastError';

export interface AuthFailure {
  /** Entra's `AADSTS…` code where present; this is the field that names the cause. */
  errorCode: string;
  errorMessage: string;
  /** Which MSAL operation failed — login, token acquisition, or the redirect handler. */
  stage: string;
  at: string;
}

/*
 * msal-browser v5 has no LOGIN_FAILURE: a failed interactive sign-in surfaces as
 * ACQUIRE_TOKEN_FAILURE, since login is a token acquisition internally. BROKERED and
 * LOGOUT failures are included because they are the other ways a session ends up absent.
 * Named from the installed EventType rather than from memory — the v4 names typecheck-fail.
 */
const FAILURE_EVENTS: readonly EventType[] = [
  EventType.ACQUIRE_TOKEN_FAILURE,
  EventType.BROKERED_REQUEST_FAILURE,
  EventType.LOGOUT_FAILURE,
];

/** Subscribes for the lifetime of the app; returns MSAL's callback id so it can be removed. */
export function captureAuthFailures(instance: IPublicClientApplication): string | null {
  return instance.addEventCallback((message: EventMessage) => {
    if (!FAILURE_EVENTS.includes(message.eventType)) return;
    if (!message.error) return;
    record(message.error as AuthError, message.eventType);
  });
}

export function record(error: AuthError | Error, stage: string): void {
  const failure: AuthFailure = {
    errorCode: 'errorCode' in error && error.errorCode ? error.errorCode : error.name,
    errorMessage:
      'errorMessage' in error && error.errorMessage ? error.errorMessage : error.message,
    stage,
    // Passed in rather than read from a clock helper: this runs in the browser only.
    at: new Date().toISOString(),
  };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(failure));
  } catch {
    /* Diagnostics must never themselves break the page. */
  }
}

export function lastAuthFailure(): AuthFailure | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const candidate = parsed as Partial<AuthFailure>;
    if (!candidate.errorCode) return null;
    return {
      errorCode: candidate.errorCode,
      errorMessage: candidate.errorMessage ?? '',
      stage: candidate.stage ?? 'unknown',
      at: candidate.at ?? '',
    };
  } catch {
    return null;
  }
}

export function clearAuthFailure(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* As above. */
  }
}
